import React, {useState, useCallback, useMemo, useEffect} from 'react';
import styles from './SummaryReports.module.scss';
import Header from 'src/components/header/Header';
import FilterLayout from './filter/FilterLayout';
import Footer from 'src/components/footer/Footer';
import { Pagination } from "antd";
import {
  SchoolReport,
  useDeleteSchoolReportMutation,
  useGenerateSchoolReportMutation,
  useGetSchoolReportDownloadUrlLazyQuery,
  useGetAccountSchoolsLazyQuery,
  useGetSchoolReportsLazyQuery,
  useListSchoolReportsLazyQuery,
} from '@api/graphql-codegen';
import Button from '@src/components/button/Button';
import DownloadIcon from '@src/assets/icons/download.svg?react';
import TrashIcon from '@src/assets/icons/trash.svg?react';
import moment from 'moment';
import { useUser } from '@hooks/useUser';
import { useToast } from '@src/hooks';
import { downloadFileWithName } from '@src/services/File';
import DeleteModal from '@src/components/modal/delete-modal/DeleteModal';
import { UserRoleEnum, SelectOption } from '@src/types';
import { SURVEY_TYPE } from '@src/constants';
import CircleLoader from '@src/components/spinner/CircleLoader';
import Tooltip from '@src/components/tooltip/Tooltip';
import OverflowTooltip from '@src/components/overflowTooltip/OverflowTooltip';
import { logger } from '../../../utils/logger';
import FooterPage from '@src/components/footer-page/FooterPage';

const pageSize = 10;

function SummaryReports() {
  const today = new Date();
  const [schoolOptions, setSchoolOptions] = useState<SelectOption[]>([]);
  const [lookupIds, setLookupIds] = useState<string[]>([])
  const [label, setLabel] = useState('')
  const [startDate, setStartDate] = useState<any>(null);
  const [endDate, setEndDate] = useState<any>(moment(today).endOf('day').format('MMM DD, YYYY HH:mm:ss'));
  const [current, setCurrent] = useState(1);
  const [minIndex, setMinIndex] = useState(0);
  const [maxIndex, setMaxIndex] = useState(pageSize);
  const [removeReport, setRemoveReport] = useState<SchoolReport | undefined>(undefined);
  const [removeConfirmation, setRemoveConfirmation] = useState(false);
  const [schoolFilter, setSchoolFilter] = useState<string[] | undefined>(undefined);
  const [reportType, setReportType] = useState('');
  const [schoolReports, setSchoolReports] = useState<SchoolReport[]>([])
  const [uiState, setUiState] = useState<{deleting: Record<string, boolean>, downloading: Record<string, boolean> }>({ deleting: {}, downloading: {} })

  const {successToast, errorToast} = useToast();
  const [me] = useUser();

  const isAussie = useMemo(() => me?.id?.startsWith("AU_"), [me?.id]);
  const isDistrictAdmin = useMemo(() => me?.role === UserRoleEnum.DistrictAdmin, [me?.role]);

  const reportTypes = useMemo(() => {
    if (isAussie) {
      return [
        {label: "Exit Ticket Summary", value: String(SURVEY_TYPE.EXIT_TICKET)},
        {label: "Pre-Program Quiz", value: String(SURVEY_TYPE.AU_PRE_PROGRAM_QUIZ)},
        {label: "Post-Unit Assessment", value: String(SURVEY_TYPE.AU_POST_PROGRAM_QUIZ)},
      ]
    }
    return [
      {label: "Exit Ticket Summary", value: String(SURVEY_TYPE.EXIT_TICKET)},
      {label: "Pre-Program Quiz", value: String(SURVEY_TYPE.NA_PRE_PROGRAM_QUIZ)},
      {label: "Post-Program Quiz", value: String(SURVEY_TYPE.NA_POST_PROGRAM_QUIZ)},
    ]
  }, [isAussie])

  const [getSchools] = useGetAccountSchoolsLazyQuery({
    fetchPolicy: 'network-only',
    onCompleted(res) {
      const allowedSchoolIds = me?.districtSchools ? JSON.parse(me?.districtSchools) : [];
      const allowedSchoolsOptions = (res?.accountSchools?.schools ?? []).filter(s => !!s?.id && allowedSchoolIds.includes(s.id)).map((s) => {
        return {
          value: s!.id,
          label: s!.name,
        };
      });
      setSchoolOptions(allowedSchoolsOptions);
    }
  });
  const [getlistSchoolReports, {data, refetch}] = useListSchoolReportsLazyQuery({fetchPolicy: 'network-only'})
  const [generateSchoolReport, {loading: generating}] = useGenerateSchoolReportMutation({
    onCompleted(res) {
      if (res.generateSchoolReport) {
        successToast('Your Summary Report is being generated.');
        refetch();
      }
      handleResetFilters();
    },
    onError(e) {
      if (e.message.includes('No exit tickets') || e.message.includes('No post program summary') || e.message.includes('No pre program summary')) {
        errorToast('There is not data available for your selected dates. Please select a different Date Range.');
      } else {
        errorToast(e.message);
      }
    }
  })
  const [getSchoolReportDownloadURL] = useGetSchoolReportDownloadUrlLazyQuery({fetchPolicy: 'network-only'})
  const [deleteSchoolReport] = useDeleteSchoolReportMutation()
  const [getSchoolReports] = useGetSchoolReportsLazyQuery()

  useEffect(() => {
    getlistSchoolReports();
  }, [getlistSchoolReports])

  const handleDownload = useCallback((url: string, name: string, startName: string, endName: string) => {
    if (!!url) {
      const fileName = `${name}-${startName}-${endName}`;
      downloadFileWithName(fileName, url, 'pdf').then(() => {
        successToast('Your Summary Report has been Successfully Created.');
      }).catch(() => {
        errorToast('Url not provided');
      })
    } else {
      errorToast('Url not provided');
    }
  }, [successToast, errorToast])

  useEffect(() => {
    if (data) {
      const _schoolReports = data.listSchoolReports ?? []
      const _lookupIds = _schoolReports.filter(s => !s.key).map(s => s.id)
      setSchoolReports(_schoolReports)
      if (_lookupIds.length) setLookupIds(_lookupIds)
    }
  }, [data])
  useEffect(() => {
    if (me?.customer?.id && me?.role === UserRoleEnum.DistrictAdmin) {
      getSchools({variables: { input: { id: me.customer.id } } });
    }
  }, [getSchools, me?.customer?.id, me?.role]);


  useEffect(() => {
    // Define a function to fetch data from the API
    const fetchData = async () => {
      try {
        if (lookupIds.length) {
          const res = await getSchoolReports({ fetchPolicy: 'network-only', variables: { ids: lookupIds } })
          if (res.data?.getSchoolReports?.length) {
            // refresh table
            refetch()
            res.data?.getSchoolReports.forEach((sr) => {
              handleDownload(sr.url ?? '', sr.name, moment(parseInt(sr.startDate)).format('MMM DD, YYYY'), moment(parseInt(sr.endDate)).format('MMM DD, YYYY'))
            })
            const ids = res.data?.getSchoolReports.map(sr => sr.id)
            const _lookupIds = lookupIds.filter(id => !ids.includes(id))
            setLookupIds(_lookupIds)
          }
        }
      } catch (error) {
        logger.error('Error polling:', error);
      }
    };

    // Call fetchData initially when the component mounts
    fetchData();

    // Set up polling using setInterval
    const pollingId = setInterval(fetchData, 5000);

    // Clean up the polling interval when the component unmounts
    return () => clearInterval(pollingId);
  }, [getSchoolReports, handleDownload, lookupIds, refetch]);

  const handleChangeSchool = useCallback((o: string[]) => {
    setSchoolFilter(o);
  }, []);
  const handleReportType = useCallback((o: SelectOption) => {
    setReportType(o.value.toString());
  }, []);
  const handlePageChange = (page: number) => {
    setCurrent(page);
    setMinIndex((page-1) * pageSize);
    setMaxIndex(page * pageSize);
  }

  const handleChangeStart = useCallback((v: string) => {
    setStartDate(v);
  }, []);
  const handleChangeEnd = useCallback((v: string) => {
    setEndDate(v);
  },[])
  const handleChangeName = useCallback((v: string) => {
    setLabel(v);
  },[])

  const handleResetFilters = useCallback(() => {
    setStartDate('');
    setSchoolFilter(undefined);
    setReportType('');
    const today = new Date();
    setEndDate(moment(today).endOf('day').format('MMM DD, YYYY HH:mm:ss'));
    setLabel('');
  }, []);
  const handleGenerateReport = useCallback(() => {
    const convertStartDate = moment(startDate, 'MMM DD, YYYY HH:mm:ss').format('YYYY-MM-DD HH:mm:ss')
    const endOfDate = new Date(endDate);
    const convertEndDate = moment(endOfDate).endOf('day').format('YYYY-MM-DD HH:mm:ss')
    const reportName = reportTypes.find(report => report.value === reportType)?.label
    const today = new Date();
    const name = label === '' ? `${reportName} ${moment(today).endOf('day').format('MMM DD, YYYY')}` : label
    const allDistSchools = schoolOptions.map(school => school.value);
    const schoolIDs = isDistrictAdmin ? schoolFilter ? schoolFilter : allDistSchools : undefined
    generateSchoolReport({
      variables: {
        input: {
          name: name,
          surveyId: reportType,
          startDate: startDate ? convertStartDate : '',
          endDate: endDate ? convertEndDate : '',
          schoolIds: schoolIDs,
        }
      }
    })
  }, [startDate, endDate, generateSchoolReport, label, isDistrictAdmin, schoolFilter, reportType, reportTypes, schoolOptions])

  const handleGenerateDownloadLink = useCallback(async(report: SchoolReport) => {
    try {
      if (!report.key) return

      setUiState((prev) => {
        prev.downloading = {
          ...prev.downloading,
          [report.id]: true
        }
        return prev
      })
      const convertStartDate = moment(parseInt(report.startDate)).format('MMM DD, YYYY')
      const convertEndDate = moment(parseInt(report.endDate)).format('MMM DD, YYYY')
      const response = await getSchoolReportDownloadURL({
        variables: {
          input: { key: report.key }
        }
      })
      if (response.data?.getSchoolReportDownloadURL) {
        handleDownload(response.data?.getSchoolReportDownloadURL, report.name, convertStartDate, convertEndDate)
      } else {
        errorToast('Download failed');
      }
    } catch (e) {
      logger.info('generating ULR error - ', e)
    } finally {
      setUiState((prev) => {
        delete prev.downloading[report.id]
        return prev
      })
    }
  }, [getSchoolReportDownloadURL, handleDownload, errorToast])

  const handleConfirmRemoveReport = useCallback(async(report: SchoolReport) => {
    setRemoveReport(report);
    setRemoveConfirmation(true);
  }, [setRemoveReport, setRemoveConfirmation])

  const handleRemoveReport = useCallback(async() => {
    if (removeReport) {
      setRemoveConfirmation(false);
      try {
        setUiState((prev) => {
          prev.deleting = {
            ...prev.deleting,
            [removeReport.id]: true
          }
          return prev
        })
        await deleteSchoolReport({
          variables: {
            input: { id: removeReport.id }
          }
        })
        await refetch();
        setRemoveReport(undefined);
        successToast(`${removeReport.name} is removed Successfully!`);
      } catch (err: any) {
        logger.error(err)
        errorToast(err?.message ?? 'Something went wrong!');
      } finally {
        setUiState((prev) => {
          delete prev.deleting[removeReport.id]
          return prev
        })
      }
    }
  }, [deleteSchoolReport, successToast, errorToast, refetch, removeReport])

  return (
    <>
      <Header/>
      <div className={styles.page}>
        <>
          <div className={styles.actions}>
            <div className={styles.title}>Reports</div>
          </div>
          <label className={styles.label}>{'Select the Report Type and date range, then click Generate Report. When the report has been finished, it will appear in the  list:'}</label>
          <FilterLayout name={label} startDate={startDate} endDate={endDate} loading={generating}
            handleChangeStart={handleChangeStart} handleChangeEnd={handleChangeEnd} handleChangeName={handleChangeName}
            handleResetFilters={handleResetFilters} handleGenerateReport={handleGenerateReport}
            schoolOptions={schoolOptions}
            isDistrictAdmin={isDistrictAdmin}
            handleChangeSchool={handleChangeSchool}
            schoolFilter={schoolFilter}
            reportType={reportType}
            handleReportType={handleReportType}
            listOptions={reportTypes}
          />

          <div className={isDistrictAdmin ? styles.widerHeaderFilter : styles.headerFilter}>
            <div className={styles.filter}>
              <div className={styles.headerTitle}>Report Type</div>
            </div>
            {isDistrictAdmin ? (
              <div className={styles.filter}>
                <div className={styles.headerTitle}>School</div>
              </div>
            ) : null}
            <div className={styles.filter}>
              <div className={styles.headerTitle}>Export Name</div>
            </div>
            <div className={styles.filter}>
              <div className={styles.headerTitle}>Date Range</div>
            </div>
            <div className={styles.dateFilter}>
              <div className={styles.headerTitle}>Date Created</div>
            </div>
            <div className={styles.filter}>
              <div className={styles.headerTitle}></div>
            </div>
          </div>
          {
            schoolReports.map((report, index) => {
              const generatedDate = moment(parseInt(report.generatedAt)).format('MMM DD, YYYY')
              const convertStartDate = moment(parseInt(report.startDate)).format('MMM DD, YYYY')
              const convertEndDate = moment(parseInt(report.endDate)).format('MMM DD, YYYY')
              const schoolName = schoolOptions.filter(option => report.schoolIds.includes(option.value)).map((opt => opt.label))
              const type = reportTypes.find(elem => elem.value === report.surveyId)?.label ?? ''
              if(index >= minIndex && index < maxIndex ) {
                return (
                  <div key={report.id} className={isDistrictAdmin ? styles.widerColumnView : styles.columnView}>
                    <div className={styles.column}>{type}</div>
                    {isDistrictAdmin ? (
                      <div className={styles.schoolColumn}>{
                        <OverflowTooltip text={schoolName.toString()} />
                      }</div>
                    ) : null}
                    <div className={styles.column}>{report.name}</div>
                    <div className={styles.column}>{convertStartDate} - {convertEndDate}</div>
                    <div className={styles.dateColumn}>{generatedDate}</div>
                    <div className={styles.buttonView}>
                      <Button
                        label=''
                        className={styles.deleteBtn}
                        leftIcon={!!uiState.deleting[report.id] ? <CircleLoader /> : <TrashIcon className={styles.trashIcon} />}
                        onClick={() => handleConfirmRemoveReport(report)}
                      />
                      <Button
                        label=''
                        className={styles.downloadBtn}
                        leftIcon={
                          !report.key ? (
                            <Tooltip
                              color="light"
                              position={'right'}
                              tooltip={'We are generating your PDF'}
                            >
                              <CircleLoader />
                            </Tooltip>
                          ) : !!uiState.downloading[report.id] ? <CircleLoader /> : <DownloadIcon className={styles.downloadIcon} />
                        }
                        onClick={() => handleGenerateDownloadLink(report)}
                      />
                    </div>
                  </div>
                );
              } else {return null}
            })
          }
          <Pagination
            pageSize={pageSize}
            current={current}
            total={schoolReports.length}
            onChange={handlePageChange}
            className={styles.pagination}
          />
        </>
        <FooterPage />
      </div>
      {/* <Footer/> */}
      {removeConfirmation ? <DeleteModal size='small' open={removeConfirmation} onClose={()=>{setRemoveConfirmation(false)}} onDelete={handleRemoveReport} message={`Are you sure you want to delete ${removeReport?.name}?`} cancelLabel={'NO'} deleteLabel={'DELETE'} /> : null}
    </>
  );
}

export default SummaryReports;
